import { useCallback } from 'react';
import { useTranslation } from '../i18n/TranslationContext';

/**
 * v2-only translation strings.
 *
 * The design plan asks for these keys to be added to
 * `src/i18n/translations/en.json` under `app.navigation.*`. This build is
 * constrained to a single edit outside `src/newui/` (the `/v2/*` route in
 * App.tsx), so the new strings live here as an overlay instead, keyed with the
 * exact dotted names the plan specifies. Moving them into en.json later is a
 * copy-paste with no call-site changes: `tv()` checks the overlay first and
 * falls through to the real `t()` for everything else, so a key that exists in
 * both resolves from en.json the moment it is added there... and until then,
 * from here.
 *
 * The nine nav labels shared with v1 are NOT duplicated here on purpose — they
 * resolve from `app.navigation.*` in en.json so a rename lands in both UIs.
 */
export const V2_STRINGS: Record<string, string> = {
  // Sidebar section labels (new)
  'app.navigation.sections.find': 'Find',
  'app.navigation.sections.inventory': 'Inventory',
  'app.navigation.sections.platforms': 'Platforms',
  'app.navigation.sections.account': 'Account',
  // Footer-zone items (new)
  'app.navigation.billing': 'Billing',
  'app.navigation.setup': 'Setup guide',
  'app.navigation.help': 'Help',
  'app.navigation.support': 'Support',
  'app.navigation.privacy': 'Privacy',
  'app.navigation.terms': 'Terms',
  // Shell chrome (new)
  'app.navigation.search': 'Search…',
  'app.navigation.mainLabel': 'Main',
  'app.navigation.openMenu': 'Open navigation',
  'app.navigation.closeMenu': 'Close navigation',
  'app.navigation.commandPalette': 'Search and navigate',
  'app.navigation.needAttention': '{count} need attention',
  'app.navigation.needsAttention': '{count} needs attention',
  'app.navigation.diceSignup': 'Create Dice Account',
  'app.navigation.seats': 'Team',
};

type Params = Record<string, string | number>;

function fill(text: string, params?: Params): string {
  if (!params) return text;
  return Object.entries(params).reduce(
    (out, [key, value]) => out.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value)),
    text,
  );
}

/**
 * `t()` for v2 surfaces: identical semantics to the app's `useTranslation().t`,
 * plus the v2-only overlay above. Never hardcode a nav label — call this.
 */
export function useV2Translation(): { t: (key: string, params?: Params) => string } {
  const { t: baseT } = useTranslation();

  const t = useCallback(
    (key: string, params?: Params): string => {
      const override = V2_STRINGS[key];
      if (override !== undefined) return fill(override, params);
      return baseT(key, params);
    },
    [baseT],
  );

  return { t };
}
